export default function ProblemSolution() {
  return (
    <section className="bg-gradient-to-br from-gray-50 to-white py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            The Old Way vs. The TeamPro Way
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stop wasting time on manual coordination. See how TeamPro transforms equipment management.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* The Problem */}
          <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center">
                <i className="ri-close-line text-red-600 text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900">The Old Way</h3>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-time-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">15+ Hours Per Week Wasted</h4>
                  <p className="text-gray-600">
                    Athletic directors spend countless hours coordinating with multiple vendors, tracking orders in spreadsheets, and following up on shipments.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-mail-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Endless Email Chains</h4>
                  <p className="text-gray-600">
                    Back-and-forth communication with vendors, lost messages, and delayed responses create frustration and inefficiency.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-file-list-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Manual Spreadsheet Tracking</h4>
                  <p className="text-gray-600">
                    Maintaining order status, inventory levels, and budget tracking across multiple spreadsheets is error-prone and time-consuming.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-error-warning-line text-red-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">No Real-Time Visibility</h4>
                  <p className="text-gray-600">
                    Without centralized tracking, it's impossible to know order status, inventory levels, or spending in real-time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* The Solution */}
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-10 shadow-xl text-white">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-3xl font-bold">The TeamPro Way</h3>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-flashlight-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold mb-2">100% Automated Processing</h4>
                  <p className="text-white/90">
                    Click to order and everything happens automatically - payment processing, order tracking, and direct shipping coordination.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-dashboard-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold mb-2">Centralized Dashboard</h4>
                  <p className="text-white/90">
                    See all orders, inventory levels, and spending in one place with real-time updates and comprehensive analytics.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-shopping-bag-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold mb-2">45+ Premium Products</h4>
                  <p className="text-white/90">
                    Access equipment from Nike, Adidas, Riddell, Under Armour, and more - all in one marketplace with consistent pricing.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-time-line text-white text-lg"></i>
                </div>
                <div>
                  <h4 className="font-bold mb-2">Save 20+ Hours Per Week</h4>
                  <p className="text-white/90">
                    Eliminate manual coordination and get your time back to focus on coaching, training, and team development.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-white/20">
              <button 
                onClick={() => {
                  const element = document.getElementById('equipment-store');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full px-8 py-4 bg-white text-teal-600 text-lg font-semibold rounded-full hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Start Saving Time Today
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
